import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faQuestionCircle } from '@fortawesome/pro-solid-svg-icons/faQuestionCircle';
import React, { useCallback, useMemo } from 'react';

import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import { useSelector } from 'react-redux';

import { Button } from 'ui-components/components/button';
import { LoadingIndicator } from 'ui-components/components/loading-indicator';
import { Modal } from 'ui-components/components/modal';
import { ModalBody } from 'ui-components/components/modal/modal-body';
import { ModalFooter } from 'ui-components/components/modal/modal-footer';
import { ModalFooterCancelButton } from 'ui-components/components/modal/modal-footer/cancel-button';
import { ModalHeader } from 'ui-components/components/modal/modal-header';
import { Tooltip } from 'ui-components/components/tooltip';
import { TooltipContent } from 'ui-components/components/tooltip/tooltip-content';
import { useAsyncSafeState } from 'ui-components/hooks/async-safe';
import { ModalFrameworkMethodsPropType } from 'ui-components/prop-types/modal-framework-methods';

import { emptyArray } from 'ui-components/default-props/empty';
import { MarketplaceIds } from '../../../enums/marketplace-ids';
import { useIsVendorOr3PL } from '../../../hooks/use-is-vendor-or-3pl';
import { linkIDs } from '../../../services/link-service/links';
import Global from '../../../store/global-selectors';
import HelpArticleLink from '../../help-article-link';
import { FulfillmentPlanSelectorList } from './fulfillment-plan-selector-list';
import { messages } from './messages';
import { PackingSlipSharingButton } from './packing-slip-sharing-button';
import style from './ship-together-destination-modal.module.less';
import { showAmazonWarning } from './show-amazon-warning';
import { WarningIcon } from './warning-icon';

export const ShipTogetherDestinationModal = ({
  onShipTogetherContinue,
  modalFrameworkMethods,
  combinedTotalItemQuantity = 0,
  fulfillmentPlansWithCombineDetails = [],
}) => {
  const selectedOrderIds = useSelector(Global.selectSelectedOrdersIds);
  const sellerServices = useSelector(Global.selectSellerServices);
  const [isLoading, setIsLoading] = useAsyncSafeState(false);
  const [selectedFulfillmentPlanId, setSelectedFulfillmentPlanId] = useAsyncSafeState(
    fulfillmentPlansWithCombineDetails?.[0]?.fulfillmentPlanId,
  );
  const isVendorOr3Pl = useIsVendorOr3PL();
  const isAuctaneFulfillmentOrders = useMemo(() => {
    return fulfillmentPlansWithCombineDetails.some((f) => parseInt(f.marketplaceId, 10) === MarketplaceIds.AuctaneFulfillment);
  }, [fulfillmentPlansWithCombineDetails]);
  const fulfillmentPlanIds = useMemo(() => {
    return fulfillmentPlansWithCombineDetails ? fulfillmentPlansWithCombineDetails.map((f) => f.fulfillmentPlanId) : emptyArray;
  }, [fulfillmentPlansWithCombineDetails]);
  const showPackingSlipSharingButton = isVendorOr3Pl && isAuctaneFulfillmentOrders;

  const onHandleClick = useCallback(() => {
    setIsLoading(true);

    const { fulfillmentPlanId, salesOrderId } =
      fulfillmentPlansWithCombineDetails.find((x) => x.fulfillmentPlanId === selectedFulfillmentPlanId) || {};
    onShipTogetherContinue(fulfillmentPlanId, salesOrderId);
  }, [setIsLoading, fulfillmentPlansWithCombineDetails, onShipTogetherContinue, selectedFulfillmentPlanId]);

  const { closeModal } = modalFrameworkMethods;

  const shouldShowAmazonWarning = useMemo(
    () => showAmazonWarning(fulfillmentPlansWithCombineDetails),
    [fulfillmentPlansWithCombineDetails],
  );

  return (
    <Modal className={style['distination-address-modal']}>
      <ModalHeader className={style['destination-address-header']} closeModal={closeModal}>
        <FormattedMessage {...messages.shipTogetherTitle} />
        <Tooltip position='bottom'>
          <div className={style['icon-wrapper']}>
            <FontAwesomeIcon className={style.icon} icon={faQuestionCircle} />
          </div>
          <TooltipContent>
            <div className={style['tooltip-content']}>
              <FormattedMessage {...messages.shipTogetherTooltip} />
            </div>
          </TooltipContent>
        </Tooltip>
      </ModalHeader>
      {shouldShowAmazonWarning && (
        <div className={style.amzWarning}>
          <i className={style.amzWarningIcon}>
            <WarningIcon />
          </i>
          <div className={style.verticalLine} />
          <FormattedMessage {...messages.noCombineShipmentSupportAmazon} />
          <HelpArticleLink articleId={linkIDs.AmazonMarketplace} className={style.amzHref}>
            Click here to view more info
          </HelpArticleLink>
        </div>
      )}
      <ModalBody className={style['destination-address-modal-body']}>
        <div>
          {isLoading && (
            <div className={style['loading-indicator-wrapper']}>
              <LoadingIndicator className={style['loading-indicator']} />
            </div>
          )}
          {!isLoading && (
            <FulfillmentPlanSelectorList
              fulfillmentPlansWithCombineDetails={fulfillmentPlansWithCombineDetails}
              onFulfillmentPlanSelected={setSelectedFulfillmentPlanId}
              selectedFulfillmentPlanId={selectedFulfillmentPlanId}
              selectedOrderIds={selectedOrderIds}
              sellerServices={sellerServices}
            />
          )}
        </div>
      </ModalBody>
      <ModalFooter className={style['modal-footer']}>
        <div className={style['total-item-qty']}>
          <FormattedMessage {...messages.shipTogetherItemQuantityLabel} />
          <span className={style['item-num']}>{combinedTotalItemQuantity}</span>
        </div>
        <div className={style['footer-buttons']}>
          <ModalFooterCancelButton />
          {showPackingSlipSharingButton ? (
            <PackingSlipSharingButton fulfillmentPlanIds={fulfillmentPlanIds} isVendorOr3Pl={isVendorOr3Pl} onClick={onHandleClick} />
          ) : (
            <Button disabled={!selectedFulfillmentPlanId} onClick={onHandleClick} theme='primary'>
              <FormattedMessage {...messages.shipTogetherCombineButton} />
            </Button>
          )}
        </div>
      </ModalFooter>
    </Modal>
  );
};

ShipTogetherDestinationModal.propTypes = {
  modalFrameworkMethods: ModalFrameworkMethodsPropType.isRequired,
  onShipTogetherContinue: PropTypes.func.isRequired,
  fulfillmentPlansWithCombineDetails: PropTypes.array,
  combinedTotalItemQuantity: PropTypes.number,
};
